<template>
  <div class="user-roles__users-list" v-loading="isLoading">
    <div class="user-roles__users-list__controls">
      <r-search-input
        :filter-text="filterText"
        @change="e => filterChange(e)"
      />
    </div>
    <scrollbar :scrolling-x="false">
      <ul class="user-roles__users-list__wrapper">
        <li
          class="user-roles__users-list__item"
          @click="beforeChangeUser('create')"
        >
          <div class="user-roles__users-list__item-wrap">
            <user-card :content="$t('user-card:create:text')" />
          </div>
        </li>
        <li
          :class="[
            'user-roles__users-list__item',
            { loading: userLoading && clickTarget.id === user.id }
          ]"
          v-for="user in filteredUsers"
          :key="user.id"
          @click="beforeChangeUser('load-user', user)"
        >
          <div
            :class="[
              'user-roles__users-list__item-wrap',
              { active: activeUser && user.id === activeUser.id }
            ]"
          >
            <user-card :user="user" />
          </div>
          <div class="user-roles__users-list__item-loader">
            <i class="el-icon-loading" />
          </div>
        </li>
        <r-text
          v-if="!filteredUsers.length"
          type="caption"
          style="padding:0 40px;"
        >
          {{ $t("users-list:users-notfound") }}
        </r-text>
      </ul>
    </scrollbar>
  </div>
</template>

<script>
import scrollbar from "@/components/scrollbar/scrollbar";
import userCard from "../renderers/user-roles-user-card";
import { userTemplate } from "../configs";
import constants from "@/constants/url";
import cloneDeep from "lodash.clonedeep";

export default {
  props: { users: Array, isLoading: Boolean },
  components: { scrollbar, userCard },
  data() {
    return {
      userLoading: false,
      filterText: "",
      clickTarget: {},

      downloadURL: "download_share_resource",
      baseURL: constants.URL
    };
  },
  watch: {
    isNewUserReady(val) {
      if (!val) return;
      const user = this.users.find(e => e.name === this.changeByName);
      if (!user) return;
      this.setActiveUser(user);
      this.$store.commit("SET_USER_ROLES_CHANGE_BY_NAME", "");
      this.$store.commit("SET_USER_ROLES_NEW_USER_READY", false);
    }
  },
  computed: {
    filteredUsers() {
      return this.users
        .filter(
          user =>
            user.name
              .toLowerCase()
              .indexOf(this.filterText.toLowerCase().trim()) > -1
        )
        .sort((a, b) => {
          if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
          if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
          return 0;
        });
    },
    activeUser() {
      return this.$store.state.userRoles.activeUser;
    },
    hasChanges() {
      return this.$store.state.userRoles.hasChanges.user ||
        this.$store.state.userRoles.hasChanges.modules ||
        this.$store.state.userRoles.hasChanges.datasources ||
        this.$store.state.userRoles.hasChanges.local_admin
        ? true
        : false;
    },
    changeByName() {
      return this.$store.state.userRoles.changeUserByName;
    },
    isNewUserReady() {
      return this.$store.state.userRoles.isNewUserReady;
    }
  },
  methods: {
    filterChange(e) {
      this.filterText = e;
    },
    beforeChangeUser(action, user) {
      if (this.hasChanges) {
        const title = this.$t("user-list:before-open:title");
        const message = this.$t("user-list:before-open:text");
        const confirmButtonText = this.$t("user-list:button-confirm");
        const cancelButtonText = this.$t("user-list:button-cancel");
        this.$confirm(message, title, {
          customClass: "r-message-box",
          closeOnPressEscape: true,
          closeOnClickModal: false,
          type: "warning",
          confirmButtonText,
          cancelButtonText
        })
          .then(() => {
            this.revertUserData();
            action === "create" ? this.createUser() : this.setActiveUser(user);
          })
          .catch(() => {});
      } else {
        action === "create" ? this.createUser() : this.setActiveUser(user);
      }
    },
    revertUserData() {
      this.$store.state.userRoles.activeUser = cloneDeep(
        this.$store.state.userRoles.initialUser
      );
    },
    createUser() {
      this.$store.commit("SET_ACTIVE_USER_ROLES_USER", {
        user: { ...userTemplate },
        modules: [],
        ds: [],
        isCreate: true
      });
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        user: { ...userTemplate },
        modules: [],
        ds: []
      });
    },
    async setActiveUser(user) {
      if (this.activeUser && user.id === this.activeUser.id) return;
      this.clickTarget = user;
      this.userLoading = true;
      this.$store.commit("SET_USER_ROLES_LOADING", true);
      const { id } = user;
      const modules = await this.$store.dispatch("POST_REQUEST", {
        url: `user/modules?user_id=${id}`
      });
      const datasources = await this.$store.dispatch("POST_REQUEST", {
        url: `user/datasources?user_id=${id}`
      });

      const userModules = modules.data;
      const userDS = datasources.data.map(item => ({
        ...item,
        _action: undefined,
        create: item.cruds[0],
        read: item.cruds[1],
        update: item.cruds[2],
        delete: item.cruds[3],
        share: item.cruds[4],
        export: item.cruds[5] || false
      }));
      userDS.forEach(ds => {
        if (ds.children) {
          ds.children.forEach(child => {
            userDS.push({
              ...child,
              _action: undefined,
              create: child.cruds[0],
              read: child.cruds[1],
              update: child.cruds[2],
              delete: child.cruds[3],
              share: child.cruds[4],
              export: child.cruds[5] || false
            });
          });
        }
      });

      this.$store.commit("SET_ACTIVE_USER_ROLES_USER", {
        datasources: datasources.data,
        user,
        modules: userModules,
        ds: userDS
      });
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        datasources: datasources.data,
        user,
        modules: userModules,
        ds: userDS
      });
      this.userLoading = false;
      this.$store.commit("SET_USER_ROLES_LOADING", false);
    }
  }
};
</script>

<style lang="scss">
.user-roles__users-list {
  height: 100%;
  box-sizing: border-box;
  padding: 16px 0 0;
  position: relative;
  overflow: hidden;
  &__controls {
    padding: 0 16px 8px 16px;
    position: relative;
    overflow: hidden;
    .r-input {
      input {
        padding-left: 32px;
      }
    }
  }
  &__wrapper {
    text-align: center;
  }
  &__item {
    padding-right: 2px;
    position: relative;
    &-wrap {
      margin-bottom: 8px;
      padding-right: 14px;
      padding-left: 16px;
      cursor: pointer;
      &.active {
        cursor: default;
        .user-card {
          &__wrapper {
            background-color: var(--accent_primary) !important;
            border: none;
            .user-card__user-avatar__image-wrapper {
              background-color: var(--white_white) !important;
              .r-icon__svg {
                fill: var(--accent_info) !important;
              }
            }
            .r-text,
            .r-text.caption-2 {
              color: var(--white_white) !important;
            }
          }
        }
      }
    }
    .user-roles__users-list__item-loader {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 228px;
      margin: 0 auto;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      visibility: hidden;
      background-color: rgba(var(--white_white), 0.15);
    }
    &.loading {
      .user-roles__users-list__item-loader {
        opacity: 1;
        visibility: visible;
        font-size: 28px;
      }
    }
  }
  .ritm-scrollbar {
    height: calc(100% - 40px);
  }
  .r-input__filter-icons {
    position: absolute;
    top: 0;
    left: 28px;
    height: calc(100% - 8px);
    width: 12px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
  }
}
</style>

<i18n>
{
  "ru": {
    "users-list:users-notfound": "По Вашему запросу пользователи не найдены",
    "user-card:create:text": "Создать новый аккаунт",

    "user-list:before-open:title": "Предупреждение",
    "user-list:before-open:text": "В случае перехода к новому пользователю, все несохраненные изменения для текущего пользователя будут потеряны. Вы уверены?",
    "user-list:button-confirm": "Да",
    "user-list:button-cancel": "Нет"
  },
  "en": {
    "users-list:users-notfound": "No users found by your request",
    "user-card:create:text": "Create new account",

    "user-list:before-open:title": "Warning",
    "user-list:before-open:text": "If you change user editing window, all unsaved changes for the current user will be lost. Are you sure?",
    "user-list:button-confirm": "Yes",
    "user-list:button-cancel": "No"
  }
}
</i18n>
